/**
 * تم مشترک نمودارهای گزارش.
 *
 * رنگ‌های سری عمداً از رنگ‌های برند پنل (Vuexy) جدا هستند: وظیفه‌ی رنگ سری
 * «تشخیص‌پذیری» است نه برندینگ. این هشت اسلات با اسکریپت اعتبارسنجی روی
 * همان سطحی که واقعاً رندر می‌شوند تست شده‌اند:
 *   روشن (سطح #ffffff) و تیره (سطح کارت Vuexy یعنی #283046)
 * و هر دو حالت همه‌ی گیت‌ها را پاس می‌کنند (کم‌ترین ΔE مجاورِ CVD:
 * ۹.۱ روشن / ۸.۴ تیره · کم‌ترین ΔE دید طبیعی: ۱۹.۶ روشن / ۱۹.۳ تیره).
 *
 * نکته‌ی مهم: ترتیب اسلات‌ها خودش مکانیزم ایمنی کوررنگی است — جای رنگ‌ها را
 * جابه‌جا نکنید و رنگ نهم اضافه نکنید. سری نهم باید در «سایر» تجمیع شود.
 *
 * سه رنگ در حالت روشن زیر نسبت کنتراست ۳:۱ هستند؛ به همین دلیل هر نمودار
 * در این پکیج «نمای جدول» دارد و لجند همیشه نمایش داده می‌شود، تا معنا
 * هیچ‌وقت فقط با رنگ منتقل نشود.
 */

const SERIES_LIGHT = [
  '#2a78d6', // ۱ آبی
  '#eb6834', // ۲ نارنجی
  '#1baf7a', // ۳ فیروزه‌ای
  '#eda100', // ۴ زرد
  '#e87ba4', // ۵ سرخابی
  '#008300', // ۶ سبز
  '#4a3aa7', // ۷ بنفش
  '#e34948', // ۸ سرخ
]

const SERIES_DARK = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
  '#9085e9',
  '#e66767',
]

// رمپ ترتیبی تک‌رنگ (آبی) برای بزرگی پیوسته: هیت‌مپ و قیف.
// روشن‌ترین پله برای مقدار نزدیک صفر است.
const SEQUENTIAL_LIGHT = ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab']
const SEQUENTIAL_DARK = ['#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6', '#3987e5', '#5598e7', '#6da7ec', '#86b6ef', '#9ec5f4']

// رنگ‌های وضعیت — رزرو‌شده، هرگز به‌عنوان رنگ سری استفاده نمی‌شوند
const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
}

const INK_LIGHT = {
  surface: '#ffffff',
  primary: '#5e5873',
  secondary: '#6e6b7b',
  muted: '#b9b9c3',
  grid: '#ebe9f1',
  axis: '#d8d6de',
}

const INK_DARK = {
  surface: '#283046',
  primary: '#d0d2d6',
  secondary: '#b4b7bd',
  muted: '#676d7d',
  grid: '#3b4253',
  axis: '#3b4253',
}

export const isDarkSkin = skin => skin === 'dark'

export const palette = skin => (isDarkSkin(skin) ? SERIES_DARK : SERIES_LIGHT)

export const sequential = skin => (isDarkSkin(skin) ? SEQUENTIAL_DARK : SEQUENTIAL_LIGHT)

export const ink = skin => (isDarkSkin(skin) ? INK_DARK : INK_LIGHT)

export const status = STATUS

/**
 * رنگ سری بر اساس «هویت» سری تعیین می‌شود نه رتبه‌اش، تا فیلترکردن یک سری
 * باعث تغییر رنگ بقیه نشود. اندیس پایدارِ سری را پاس بدهید، نه شماره ردیف.
 */
export const seriesColor = (index, skin) => {
  const colors = palette(skin)
  return colors[index % colors.length]
}

/**
 * گزینه‌های پایه‌ی ApexCharts: شبکه‌ی مویی، بدون خط‌چین، فونت فارسی،
 * راست‌به‌چپ. هر نمودار فقط تفاوت‌های خودش را روی این می‌ریزد.
 */
export const baseChartOptions = skin => {
  const c = ink(skin)

  return {
    chart: {
      fontFamily: 'IranSans, system-ui, sans-serif',
      foreColor: c.secondary,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, easing: 'easeout', speed: 300 },
      parentHeightOffset: 0,
    },
    // خط‌چین در شبکه ممنوع است: نویز بصری اضافه می‌کند و مثل «آستانه» خوانده می‌شود
    grid: {
      borderColor: c.grid,
      strokeDashArray: 0,
      padding: {
        top: 0, right: 8, bottom: 0, left: 8,
      },
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    dataLabels: { enabled: false },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontFamily: 'IranSans, system-ui, sans-serif',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 10 },
      itemMargin: { horizontal: 10, vertical: 4 },
      labels: { colors: c.secondary },
    },
    xaxis: {
      axisBorder: { color: c.axis },
      axisTicks: { color: c.axis },
      labels: { style: { colors: c.muted, fontSize: '11px' } },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: { style: { colors: c.muted, fontSize: '11px' } },
    },
    tooltip: {
      theme: isDarkSkin(skin) ? 'dark' : 'light',
      style: { fontFamily: 'IranSans, system-ui, sans-serif' },
      shared: true,
      intersect: false,
    },
    noData: {
      text: 'داده‌ای برای این بازه وجود ندارد',
      style: { color: c.muted, fontFamily: 'IranSans, system-ui, sans-serif', fontSize: '13px' },
    },
  }
}

export default {
  palette, sequential, ink, status, seriesColor, baseChartOptions, isDarkSkin,
}
